import React, { Fragment } from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  HubNavigatorRoutePropType,
  HubNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { IS_MOBILE, parseApiError, passIf } from '@utils';
import {
  AnalyticsService,
  ShareService,
  ToastService,
} from '@services';
import { Separator, ResponsiveIconTouchable } from '@components';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useLink } from '@hooks';
import { Course } from '@models/Courses/CourseModel';

const Container = styled(View)({
  flexDirection: 'row',
  justifyContent: ['space-between', 'flex-start'],
});

const ActionGroupContainer = styled(View)({
  flexDirection: 'row',
});
export interface IActionPanelProps {
  tutorial: Tutorial;
  course?: Course;
}

type NavigationRoutePropType = HubNavigatorRoutePropType<'Tutorial'>;
type NavigationPropType = HubNavigatorNavigationPropType<'Tutorial'>;

export const ActionPanel = observer<IActionPanelProps>(
  ({ tutorial, course }) => {
    const route = useRoute<NavigationRoutePropType>();
    const nav = useNavigation<NavigationPropType>();
    const { buildFullLinkForRoute } = useLink();
    const { t } = useTranslation();
    const viewer = useViewerModel();

    async function toggleSave() {
      try {
        if (!tutorial.userStats?.isSaved) {
          ToastService.showSuccess(t('tutorial.videoSaved'));
          AnalyticsService.trackContentSave({
            contentId: tutorial.id,
            contentTitle: tutorial.title,
            tagIds: tutorial.tags.ids,
            tagNames: tutorial.tags.names,
            ...(course ? { courseId: course.id } : {}),
          });
        }

        await tutorial.toggleSave.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    async function toggleLike() {
      try {
        if (!tutorial.userStats?.isLiked) {
          AnalyticsService.trackContentLike({
            contentId: tutorial.id,
            contentTitle: tutorial.title,
            tagIds: tutorial.tags.ids,
            tagNames: tutorial.tags.names,
            ...(course ? { courseId: course.id } : {}),
          });
        }

        await tutorial.toggleLike.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    async function shareTutorial() {
      try {
        const inviteLink = buildFullLinkForRoute(route.name, {
          id: route.params.id,
          invite: viewer.currentUser?.invite.hub,
        });

        const link = await viewer.createShortLink.run({
          link: inviteLink,
        });

        if (!link) {
          throw new Error(t('errors.sharedLink'));
        }

        await ShareService.shareUrl(
          { url: link.shortLink },
          { clipboardMessage: t('hub.videoUrlCopied') },
        );

        AnalyticsService.trackContentShare({
          contentId: tutorial.id,
          contentTitle: tutorial.title,
          tagIds: tutorial.tags.ids,
          tagNames: tutorial.tags.names,
          ...(course ? { courseId: course.id } : {}),
        });
      } catch (error) {
        const message = (error as Error).message;
        ToastService.showError(message ?? t('errors.default'));
      }
    }

    function navigateToComments() {
      nav.navigate(screens.Comment, {
        list: tutorial.comments,
      });
    }

    const saveIconName = tutorial.userStats?.isSaved
      ? 'bookmark-bold'
      : 'bookmark-light';

    const likeIconName = tutorial.userStats?.isLiked
      ? 'heart-bold'
      : 'heart-light';

    const likesCount = t('tutorial.like', {
      count: tutorial.likes,
    });

    const commentsCount = t('tutorial.comment', {
      count: tutorial.comments.totalCount,
    });

    return (
      <Container>
        <ActionGroupContainer>
          <ResponsiveIconTouchable
            onPress={toggleLike}
            name={likeIconName}
            color={passIf(tutorial.userStats?.isLiked, '$error300')}
            title={likesCount}
            isResponsiveTitle={false}
          />
          <Separator size={['$8', '$10']} vertical />
          {/* TODO: may should we make this condition is responsive? */}
          {IS_MOBILE && (
            <Fragment>
              <ResponsiveIconTouchable
                onPress={navigateToComments}
                name="comment-light"
                title={commentsCount}
                isResponsiveTitle={false}
              />
              <Separator size={[null, '$10']} vertical />
            </Fragment>
          )}
        </ActionGroupContainer>

        <ActionGroupContainer>
          <ResponsiveIconTouchable
            onPress={shareTutorial}
            name="link-light"
            isLoading={viewer.createShortLink.inProgress}
            title={t('tutorial.share')}
          />
          <Separator size={['$8', '$10']} vertical />
          <ResponsiveIconTouchable
            onPress={toggleSave}
            name={saveIconName}
            title={t('tutorial.save')}
          />
        </ActionGroupContainer>
      </Container>
    );
  },
);
